import React, { useState } from 'react';
import { Card, Table, Button, Space, Tag, Input, Select, Upload, Alert, Empty, Segmented } from 'antd';
import { FilePdfOutlined, SearchOutlined, FilterOutlined, UploadOutlined, EyeOutlined, DeleteOutlined, AppstoreOutlined, UnorderedListOutlined, DownloadOutlined } from '@ant-design/icons';
import { Row, Col, Avatar } from 'antd';
import DeleteConfirmModal from './DeleteConfirmModal';
import DocumentThumbnail from './DocumentThumbnail';
import BatchOperations, { BatchCheckbox } from './BatchOperations';
import { API_BASE_URL } from '../utils/api';
import '../styles/app-redesign.css';

const { Search } = Input;

const DocumentsManager = ({ documents, projects, selectedProject, onViewPDF, onDeleteDocument, onUploadClick, onClearProject }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('thumbnails');
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [selectedDocuments, setSelectedDocuments] = useState([]);

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.original_filename.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'processed' && doc.processed) ||
                         (filterStatus === 'pending' && !doc.processed);
    return matchesSearch && matchesFilter;
  });

  const columns = [
    {
      title: 'Document',
      dataIndex: 'original_filename',
      key: 'filename',
      render: (text, record) => (
        <Space>
          <Avatar 
            icon={<FilePdfOutlined />}
            style={{ background: '#f0f0f0', color: '#9333ea' }}
          />
          <div>
            <div style={{ fontWeight: 175, color: '#171717' }}>{text}</div>
            <div style={{ fontSize: '12px', color: '#a3a3a3' }}>
              {(record.file_size / 1024 / 1024).toFixed(2)} MB
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Project',
      dataIndex: 'project_id',
      key: 'project',
      render: (projectId) => {
        const project = projects.find(p => p.id === projectId);
        return project ? (
          <Tag color="default">{project.name}</Tag>
        ) : (
          <span style={{ color: '#a3a3a3' }}>Unknown</span>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'processing_status',
      key: 'status',
      render: (status, record) => {
        const statusConfig = {
          'pending': { color: 'orange', text: 'Pending' },
          'processing': { color: 'blue', text: 'Processing' },
          'completed': { color: 'green', text: 'Completed' },
          'failed': { color: 'red', text: 'Failed' }
        };
        const config = statusConfig[status] || statusConfig.pending;
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: 'Pages',
      dataIndex: 'page_count',
      key: 'pages',
      render: (count) => count || '-'
    },
    {
      title: 'Uploaded',
      dataIndex: 'created_at',
      key: 'created',
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 280,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => onViewPDF(record)}
          >
            View
          </Button>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => window.open(`${API_BASE_URL}/documents/${record.id}/file`, '_blank')}
          >
            Download
          </Button>
          <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteClick(record)}
            >
              Delete
            </Button>
        </Space>
      )
    }
  ];

  const handleDeleteClick = (document) => {
    setDocumentToDelete(document);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = () => {
    if (documentToDelete) {
      onDeleteDocument(documentToDelete.id);
      setDeleteModalVisible(false);
      setDocumentToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setDocumentToDelete(null);
  };

  const handleBatchDelete = async (documentIds) => {
    try {
      for (const id of documentIds) {
        await onDeleteDocument(id);
      }
      setSelectedDocuments([]);
    } catch (error) {
      console.error('Batch delete failed:', error);
    }
  };

  const handleBatchTag = async (documentIds, tags) => {
    // This would need backend implementation
    console.log('Batch tag:', documentIds, tags);
    // For now, just show success message
    message.success('Tags added successfully (backend implementation needed)');
  };

  const handleBatchMove = async (documentIds, projectId) => {
    // This would need backend implementation
    console.log('Batch move:', documentIds, projectId);
    // For now, just show success message
    message.success('Documents moved successfully (backend implementation needed)');
  };

  const handleItemSelect = (itemId, checked) => {
    if (checked) {
      setSelectedDocuments([...selectedDocuments, itemId]);
    } else {
      setSelectedDocuments(selectedDocuments.filter(id => id !== itemId));
    }
  };

  const GridView = () => (
    <Row gutter={[24, 24]}>
      {filteredDocuments.map((doc) => (
        <Col xs={24} sm={12} lg={8} xl={6} key={doc.id}>
          <Card
            hoverable
            className="modern-card"
            style={{ height: '100%' }}
            cover={
              <div style={{
                height: '160px',
                background: 'linear-gradient(135deg, #f0f0f0 0%, #e5e5e5 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FilePdfOutlined style={{ fontSize: '64px', color: '#9333ea' }} />
              </div>
            }
          >
            <div style={{ marginBottom: '12px' }}>
              <h4 style={{ 
                fontSize: '14px', 
                fontWeight: 175, 
                color: '#171717',
                marginBottom: '4px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {doc.original_filename}
              </h4>
              <p style={{ fontSize: '12px', color: '#a3a3a3', margin: 0 }}>
                {(doc.file_size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <Tag color={doc.processed ? 'success' : 'processing'}>
                {doc.processed ? 'Processed' : 'Processing'}
              </Tag>
            </div>
            <Space size="small" style={{ width: '100%', justifyContent: 'space-between' }}>
              <Button 
                type="text" 
                size="small"
                icon={<EyeOutlined />}
                onClick={() => onViewPDF(doc)}
              >
                View
              </Button>
              <Space size="small">
                <Button 
                  type="text" 
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() => window.open(`${API_BASE_URL}/documents/${doc.id}/file`, '_blank')}
                />
                <Button 
                  type="text" 
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteClick(doc)}
                />
              </Space>
            </Space>
          </Card>
        </Col>
      ))}
    </Row>
  );

  return (
    <div className="app-content-redesign">
      {/* Header */}
      <div className="dashboard-header-redesign">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
          <div>
            <h1 className="dashboard-title">Documents</h1>
            <p className="dashboard-subtitle">
              Manage and view your construction plan PDFs
            </p>
          </div>
          <button 
            className="section-action-btn"
            onClick={onUploadClick}
          >
            <UploadOutlined />
            <span>Upload PDF</span>
          </button>
        </div>

        {/* Selected Project Alert */}
        {selectedProject && (
          <Alert
            message={
              <Space>
                <span>Uploading to: <strong>{selectedProject.name}</strong></span>
                <Button size="small" type="link" onClick={onClearProject}>
                  Clear
                </Button>
              </Space>
            }
            type="info"
            style={{ marginBottom: '16px' }}
            showIcon
          />
        )}

        {/* Search, Filters, and View Toggle */}
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Search
              placeholder="Search documents by filename..."
              prefix={<SearchOutlined style={{ color: '#a3a3a3' }} />}
              size="large"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%' }}
              allowClear
            />
          </Col>
          <Col>
            <Select
              size="large"
              value={filterStatus}
              onChange={setFilterStatus}
              style={{ width: 160 }}
              suffixIcon={<FilterOutlined />}
            >
              <Select.Option value="all">All Status</Select.Option>
              <Select.Option value="processed">Processed</Select.Option>
              <Select.Option value="pending">Pending</Select.Option>
            </Select>
          </Col>
          <Col>
            <Segmented
              size="large"
              value={viewMode}
              onChange={setViewMode}
              options={[
                { label: 'Thumbnails', value: 'thumbnails', icon: <AppstoreOutlined /> },
                { label: 'List', value: 'list', icon: <UnorderedListOutlined /> },
                { label: 'Grid', value: 'grid', icon: <FilePdfOutlined /> },
              ]}
            />
          </Col>
        </Row>
      </div>

      {/* Batch Operations */}
      {filteredDocuments.length > 0 && (
        <BatchOperations
          items={filteredDocuments}
          selectedItems={selectedDocuments}
          onSelectionChange={setSelectedDocuments}
          onDelete={handleBatchDelete}
          onTag={handleBatchTag}
          onMove={handleBatchMove}
          itemType="documents"
          projects={projects}
        />
      )}

      {/* Documents Display */}
      {filteredDocuments.length > 0 ? (
        viewMode === 'thumbnails' ? (
          <div className="thumbnail-grid">
            {filteredDocuments.map((doc) => (
              <div key={doc.id} style={{ position: 'relative' }}>
                <BatchCheckbox
                  itemId={doc.id}
                  isSelected={selectedDocuments.includes(doc.id)}
                  onSelect={handleItemSelect}
                  style={{ position: 'absolute', top: '8px', left: '8px', zIndex: 10 }}
                />
                <DocumentThumbnail
                  documentId={doc.id}
                  fileName={doc.original_filename}
                  onView={() => onViewPDF(doc)}
                  width={220}
                  height={280}
                  showFileName={true}
                />
              </div>
            ))}
          </div>
        ) : viewMode === 'list' ? (
          <div className="modern-table">
            <Table 
              dataSource={filteredDocuments}
              columns={columns}
              rowKey="id"
              pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `Total ${total} documents` }}
            />
          </div>
        ) : (
          <GridView />
        )
      ) : (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FilePdfOutlined />
          </div>
          <h3 className="empty-state-title">
            {searchTerm || filterStatus !== 'all' ? 'No documents found' : 'No documents yet'}
          </h3>
          <p className="empty-state-description">
            {searchTerm || filterStatus !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'Upload your first construction plan PDF to get started'
            }
          </p>
          {!searchTerm && filterStatus === 'all' && (
            <Button 
              type="primary" 
              icon={<UploadOutlined />}
              size="large"
              onClick={onUploadClick}
              style={{ 
                background: 'linear-gradient(135deg, #877689 0%, #6b5d6f 100%)',
                border: 'none',
                fontWeight: 175
              }}
            >
              Upload Your First PDF
            </Button>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        visible={deleteModalVisible}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        itemType="PDF"
        itemName={documentToDelete?.original_filename || ''}
        description="This action cannot be undone."
      />
    </div>
  );
};

export default DocumentsManager;
